import { forwardRef } from 'react'
import { noop } from 'lodash'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import {
  FormItem,
  FormLabel,
  FormMessage,
  TooltipWrapper,
  FormSelectTrigger,
  FormSelectContent,
  FormSelectItem,
  FormSelectValue,
  FormSelect,
} from '@wellesley/ui'
import {
  type EventPrivacy,
  EVENT_PRIVACY_VALUES,
  GROUP_EVENT_PRIVACY_VALUES,
} from '../../helpers/Events.utils'

type PrivacyFieldProps = Partial<
  Omit<ControllerRenderProps<FieldValues, string>, 'value' | 'onChange' | 'ref'>
> & {
  isEdit: boolean
  isGroupEvent?: boolean
  value: EventPrivacy | undefined
  onChange?: (value: EventPrivacy) => void
}

export const PrivacyField = forwardRef<HTMLButtonElement, PrivacyFieldProps>(
  ({ isEdit, isGroupEvent, onChange, ...rest }, ref) => {
    const privacyOptions = isGroupEvent
      ? GROUP_EVENT_PRIVACY_VALUES
      : EVENT_PRIVACY_VALUES

    return (
      <FormItem className="mt-none space-y-xxs">
        <FormLabel>Who can join?</FormLabel>
        <TooltipWrapper
          theme="dark"
          size="small"
          triggerClassname="w-full"
          tooltipText={isEdit ? 'Event visibility can not be changed' : ''}
          triggerProps={{
            asChild: true,
          }}
        >
          <div>
            <FormSelect onValueChange={onChange || noop} {...rest}>
              <FormSelectTrigger
                ref={ref}
                className="w-full text-base"
                data-testid="privacy-field"
              >
                <FormSelectValue placeholder="Select visibility" />
              </FormSelectTrigger>
              <FormSelectContent>
                {privacyOptions.map(({ icon: Icon, ...opt }) => (
                  <FormSelectItem key={opt.key} value={opt.key}>
                    <div className="flex items-center gap-xxs px-xxs">
                      <Icon size={20} />
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-gray-900">
                          {opt.title}
                        </span>
                        <span className=" text-gray-500">
                          {opt.description}
                        </span>
                      </div>
                    </div>
                  </FormSelectItem>
                ))}
              </FormSelectContent>
            </FormSelect>
          </div>
        </TooltipWrapper>
        <FormMessage />
      </FormItem>
    )
  },
)

PrivacyField.displayName = 'PrivacyField'
